import type { ChartDatum } from '../types'

// MP-OS-207 — partial periods (period_kind 'partial', e.g. the running month) are drawn
// differently from full ones, not only labelled: a dashed line segment into a hollow dot,
// and a lighter, dashed-outline bar. Values are never changed; only how they are drawn.
// Pure so the derived series are unit-tested without rendering recharts.

export const PARTIAL_SUFFIX = '__partial'
export const partialKey = (key: string): string => `${key}${PARTIAL_SUFFIX}`

// MP-OS-207 (zero days) — a bucket with days whose data is not verified yet comes with
// `coverage: 'incomplete'` and `missing_days`; its value is the sum of the verified days only.
// It is not a complete period, so it is drawn exactly like a partial one. A verified day without
// sales is a real 0; a missing day is a gap (null), never a zero.
// MPD-133 L7: a cumulative, rolling-average or indexed value that includes an incomplete bucket is
// understated too, so it is marked incomplete with `coverage_via: 'derived'` and `missing_days` =
// the missing days it includes (landing.transformSeries / indexSeries).
export const COVERAGE_KEYS = ['coverage', 'missing_days', 'coverage_via'] as const
export const isIncompletePoint = (d: ChartDatum | undefined): boolean => d?.coverage === 'incomplete'
export const isDerivedIncomplete = (d: ChartDatum | undefined): boolean => isIncompletePoint(d) && d?.coverage_via === 'derived'
export const missingDays = (d: ChartDatum | undefined): number =>
  typeof d?.missing_days === 'number' && Number.isFinite(d.missing_days) ? d.missing_days : 0
export const missingDaysText = (n: number): string => `${n} missing ${n === 1 ? 'day' : 'days'}`
// MPD-141 F-L3: a derived point's own period is complete; the missing days are in the periods it
// includes, so it reads "includes incomplete periods", never "incomplete (N missing days)".
export const DERIVED_INCOMPLETE_TEXT = 'includes incomplete periods'
/** The coverage wording of an incomplete point (CSV Coverage column, tooltip). */
export const incompleteText = (d: ChartDatum | undefined): string =>
  isDerivedIncomplete(d) ? DERIVED_INCOMPLETE_TEXT : `incomplete (${missingDaysText(missingDays(d))})`
export const isPartialPoint = (d: ChartDatum | undefined): boolean => d?.period_kind === 'partial' || isIncompletePoint(d)

// Bar look for a partial bar (Recharts Cell props), added on top of its normal fill.
export const PARTIAL_BAR = { fillOpacity: 0.4, strokeDasharray: '4 3', strokeWidth: 1.5 } as const
// Dash for the partial segment of a solid series; a dashed (competitor) series keeps its own
// dash and is made lighter instead, so its pattern stays legible.
export const PARTIAL_DASH = '4 4'
export const PARTIAL_OPACITY = 0.5

/**
 * Line data split into a solid part and a dashed partial part per series.
 *
 * For each series key, `key` keeps only the full periods (the solid line stops at the last
 * full point) and `${key}__partial` holds every partial point plus its full neighbours, so the
 * dashed segment connects to the solid line. Rows without a partial neighbour leave the
 * partial key unset (a gap, never a zero). Returns null when no row is partial, so a chart
 * without partial periods renders exactly as before.
 */
export function splitPartialLine(data: ChartDatum[], keys: readonly string[]): ChartDatum[] | null {
  if (!data.some(isPartialPoint)) return null
  const inPartialSegment = (i: number): boolean =>
    isPartialPoint(data[i]) || isPartialPoint(data[i - 1]) || isPartialPoint(data[i + 1])
  return data.map((row, i) => {
    const out: ChartDatum = { ...row }
    for (const key of keys) {
      const value = row[key] ?? null
      if (inPartialSegment(i)) out[partialKey(key)] = value
      if (isPartialPoint(row)) out[key] = null
    }
    return out
  })
}
